/**
 * Personal Details card (design §8) — identity fields from the
 * ApplicationSummary DTO only; no step parsing here.
 */
import {
	applicationTypeLabel,
	splitName,
} from '#app/utils/application-detail-v2/display'
import { childAgeLabel } from '#app/utils/application-detail-v2/display'
import type { ApplicationSummary } from '#app/utils/application-detail-v2/types'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import { FieldBox, SectionCard } from '../primitives'

export function PersonalDetails(props: {
	application: ApplicationSummary
	propertyStep?: ParsedStepData['property']
}) {
	const { application, propertyStep } = props
	const { formatShortDate } = useFormatDate()
	const isGuarantor = application.applicationType === 'guarantor'
	const isStudentHousing =
		!isGuarantor && propertyStep?.isStudentHousing === true

	const adultDob =
		typeof propertyStep?.adults?.[0]?.dateOfBirth === 'string'
			? propertyStep.adults[0].dateOfBirth
			: ''
	const normalDob = isStudentHousing ? '' : adultDob

	const occupant0 = propertyStep?.adults?.[0]

	const studentFirstName =
		typeof occupant0?.firstName === 'string' ? occupant0.firstName : ''
	const studentLastName =
		typeof occupant0?.lastName === 'string' ? occupant0.lastName : ''
	const studentEmail =
		typeof occupant0?.email === 'string' ? occupant0.email : ''
	const studentPhone =
		typeof occupant0?.phone === 'string' ? occupant0.phone : ''
	const studentDob =
		typeof occupant0?.dateOfBirth === 'string' ? occupant0.dateOfBirth : ''
	const studentGender =
		typeof occupant0?.gender === 'string' ? occupant0.gender : ''
	const studentSchoolProgram =
		typeof occupant0?.schoolProgram === 'string' ? occupant0.schoolProgram : ''
	const studentStudyLevelAndGraduation =
		typeof occupant0?.studyLevelAndGraduation === 'string'
			? occupant0.studyLevelAndGraduation
			: ''
	const studentRelationship =
		typeof occupant0?.relationship === 'string' ? occupant0.relationship : ''

	const { firstName, lastName } = splitName(application.applicantName)

	return (
		<SectionCard
			iconSrc="/img/personal-details.svg"
			// Figma reads "Occupant Details" for student housing (QA 2026-09-08).
			title={isStudentHousing ? 'Occupant Details' : 'Personal Details'}
			columns={isGuarantor ? 3 : 2}
		>
			{isStudentHousing ? (
				<>
					<FieldBox label="First Name" value={studentFirstName} />
					<FieldBox label="Last Name" value={studentLastName} />
					<FieldBox label="Relationship" value={studentRelationship} />
					<FieldBox label="Gender" value={studentGender} />
					<FieldBox
						label="Date Of Birth"
						value={
							studentDob
								? (() => {
										const age = childAgeLabel(studentDob, new Date())
										const formatted = formatShortDate(studentDob)
										return age ? `${formatted} (${age})` : formatted
									})()
								: ''
						}
					/>
					<FieldBox label="Email" value={studentEmail} />
					<FieldBox
						label="Phone"
						value={formatPhoneNumberWithFallback(studentPhone)}
					/>
					<FieldBox
						label="Type"
						value={applicationTypeLabel(application.applicationType)}
					/>
					<FieldBox label="School & Program" value={studentSchoolProgram} />
					<FieldBox
						label="Study Level & Graduation"
						value={studentStudyLevelAndGraduation}
					/>
				</>
			) : (
				<>
					<FieldBox label="First Name" value={firstName} />
					<FieldBox label="Last Name" value={lastName} />
					<FieldBox label="Email" value={application.applicantEmail} />
					<FieldBox
						label="Phone"
						value={formatPhoneNumberWithFallback(application.applicantPhone)}
					/>
					<FieldBox
						label="Date Of Birth"
						value={normalDob ? formatShortDate(normalDob) : ''}
					/>
					<FieldBox
						label="Type"
						value={applicationTypeLabel(application.applicationType)}
					/>
				</>
			)}
		</SectionCard>
	)
}
